// apps/web/src/components/painel/home/precisa-de-voce.tsx — Fase 17.8, Plano 07 (D-09, D-22; UI-SPEC U-05, U-12, U-24, U-26).
// Painel "Precisa de você" (a Diretoria tem título próprio): até 5 itens por urgência, cada um UM único link (<a>) para o registro
// ou a lista (D-13 da 16.1: nenhuma ação executa no painel), com tag de categoria, título, detalhe e "quando" com prefixo oculto
// de urgência. Acima de 5, "Ver todos (N)" expande no próprio painel até 20 linhas. A linha vira <div> (sem seta) quando a
// sessão não pode abrir a rota (`podeAbrirRota`). Vazio = uma linha com CheckCircle2; carregando = 5 linhas de esqueleto; falha
// da região = RegiaoErro. A decisão visual (rótulos, contagem, prefixo) vem de lib/painel/home/lista.ts.
// Ao vivo (Plano 12): quando a lista muda por evento do stream, o item novo entra com fade + 4px (200 ms) e brilho, o item
// removido sai com fade (160 ms) antes de desmontar e a reordenação usa FLIP com transform (200 ms). Movimento reduzido: nada
// anima e o item novo recebe o contorno estático.
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement, type RefObject } from 'react';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import type { PerfilHome, PrecisaDeVoceItem, RegiaoPrecisaDeVoce } from '@pieltec/contracts';
import { podeAbrirRota } from '../../../lib/painel/blocos';
import { atributosDoBrilho, diferencaDeIds } from '../../../lib/painel/home/brilho';
import { LIMITE_LINHAS_RECOLHIDA, prefixoUrgencia, rotuloContagem, rotuloQuando, rotuloVerTodos } from '../../../lib/painel/home/lista';
import { EstadoVazio } from './estado-vazio';
import { RegiaoCarregando, RegiaoErro } from './regiao-estado';
import { TagCategoria } from './tag-categoria';
import '../../../styles/painel-home.css';

export const TEXTO_ESTADO_VAZIO = 'Nada pede sua atenção agora.';

/** Altura do esqueleto: cabeçalho de 56px e 5 linhas de 56px (U-26), para a lista não deslocar o layout ao carregar. */
const ALTURA_ESQUELETO = 56 * (LIMITE_LINHAS_RECOLHIDA + 1);

/** Duração da entrada do item novo e da reordenação (FLIP), e da saída do item removido (UI-SPEC, tabela de movimento). */
const DURACAO_ENTRADA_MS = 200;
const DURACAO_SAIDA_MS = 160;
const DURACAO_FLIP_MS = 200;
const CURVA_FLIP = 'cubic-bezier(.2,.7,.3,1)';

type EstadoLinha = 'entrando' | 'saindo';

/** Item que acabou de sair da lista e fica, apagando, na posição em que estava até terminar o fade. */
export interface ItemSaindo {
  readonly item: PrecisaDeVoceItem;
  readonly indice: number;
}

/** Título do painel por perfil (U-05). */
export function tituloDoPainel(perfil: PerfilHome): string {
  return perfil === 'diretoria' ? 'Para sua decisão' : 'Precisa de você';
}

/** Reinsere os itens que estão saindo nas posições em que estavam. */
function comSaindo(
  itens: readonly PrecisaDeVoceItem[],
  saindo: readonly ItemSaindo[],
): readonly { readonly item: PrecisaDeVoceItem; readonly saindo: boolean }[] {
  const linhas = itens.map((item) => ({ item, saindo: false }));
  for (const fantasma of [...saindo].sort((a, b) => a.indice - b.indice)) {
    linhas.splice(Math.min(fantasma.indice, linhas.length), 0, { item: fantasma.item, saindo: true });
  }
  return linhas;
}

interface LinhaProps {
  readonly item: PrecisaDeVoceItem;
  readonly agora: Date;
  readonly permissoes: readonly string[];
  readonly estado?: EstadoLinha;
  readonly brilhos?: readonly string[];
  readonly reduzido?: boolean;
}

function Linha({ item, agora, permissoes, estado, brilhos, reduzido = false }: LinhaProps): ReactElement {
  const quando = item.quando === undefined ? '' : rotuloQuando(item.quando, agora);
  const corpo = (
    <>
      <TagCategoria tag={item.tag} />
      <span className="painel-item__texto">
        <span className="painel-item__titulo">{item.titulo}</span>
        {item.detalhe !== undefined && <span className="painel-item__detalhe">{item.detalhe}</span>}
      </span>
      {quando !== '' && (
        <span className="painel-item__quando" data-urgencia={item.urgencia}>
          <span className="sr-only">{prefixoUrgencia(item.urgencia)}</span>
          {quando}
        </span>
      )}
    </>
  );
  const atributos = { 'data-testid': `painel-item-${item.id}`, 'data-item-id': item.id } as const;
  const saindo = estado === 'saindo';
  const classe = estado === undefined ? 'painel-item' : `painel-item painel-item--${estado}`;
  return (
    <li
      className={classe}
      data-urgencia={item.urgencia}
      aria-hidden={saindo ? true : undefined}
      {...atributos}
      {...atributosDoBrilho(`item-${item.id}`, brilhos, reduzido)}
    >
      {/* O item que está saindo vira linha sem link: não é mais um destino focável durante o fade. */}
      {!saindo && podeAbrirRota(item.href, permissoes) ? (
        <a className="painel-item__linha painel-item__linha--link" href={item.href}>
          {corpo}
          <ChevronRight className="painel-item__seta" aria-hidden="true" />
        </a>
      ) : (
        <div className="painel-item__linha">{corpo}</div>
      )}
    </li>
  );
}

export interface PrecisaDeVoceVisaoProps {
  readonly perfil: PerfilHome;
  /** `null` enquanto a carga está em andamento (esqueleto). */
  readonly regiao: RegiaoPrecisaDeVoce | null;
  readonly permissoes: readonly string[];
  /** Instante de referência do "quando" (o `geradoEm` da resposta: determinístico e coerente com os dados). */
  readonly agora: Date;
  readonly expandido: boolean;
  readonly onAlternar: () => void;
  readonly onTentarNovamente: () => void;
  /** Ids de card que brilham agora (`item-{id}`), vindos de `mudancasEntre`. */
  readonly brilhos?: readonly string[];
  /** Movimento reduzido: o item novo recebe o contorno estático em vez do brilho animado. */
  readonly reduzido?: boolean;
  /** Ids dos itens que acabaram de entrar (classe de entrada). */
  readonly entrando?: ReadonlySet<string>;
  /** Itens que acabaram de sair e continuam na tela, apagando. */
  readonly saindo?: readonly ItemSaindo[];
  /** Ref da `<ul>`, para medir as posições do FLIP. */
  readonly listaRef?: RefObject<HTMLUListElement | null>;
}

/** Visão controlada: o estado de expansão vem de fora (testável por SSR); `PrecisaDeVoce` guarda o estado. */
export function PrecisaDeVoceVisao({
  perfil,
  regiao,
  permissoes,
  agora,
  expandido,
  onAlternar,
  onTentarNovamente,
  brilhos,
  reduzido = false,
  entrando,
  saindo = [],
  listaRef,
}: PrecisaDeVoceVisaoProps): ReactElement | null {
  const idLista = useId();
  const idTitulo = useId();
  const titulo = tituloDoPainel(perfil);

  // Nada foi verificado (a sessão não tem permissão em nenhuma categoria): não afirma "Nada pede sua atenção".
  if (regiao !== null && regiao.estado === 'SEM_MEDICAO') return null;

  const cabecalho = (contagem?: string): ReactElement => (
    <header className="painel-lista__cabecalho">
      <h2 id={idTitulo} className="painel-lista__titulo">
        <span className="painel-lista__marcador" aria-hidden="true" />
        {titulo}
      </h2>
      {contagem !== undefined && (
        <span className="painel-lista__contagem" data-testid="painel-precisa-de-voce-contagem">
          {contagem}
        </span>
      )}
    </header>
  );

  if (regiao === null) {
    return (
      <section className="painel-lista" data-testid="painel-precisa-de-voce" aria-labelledby={idTitulo} aria-busy="true">
        {cabecalho()}
        <RegiaoCarregando id="lista" altura={ALTURA_ESQUELETO - 56} />
      </section>
    );
  }
  if (regiao.estado === 'INDISPONIVEL') {
    return (
      <section className="painel-lista" data-testid="painel-precisa-de-voce" aria-labelledby={idTitulo}>
        {cabecalho()}
        <div className="painel-lista__erro">
          <RegiaoErro id="lista" nome={`a lista "${titulo}"`} onTentarNovamente={onTentarNovamente} />
        </div>
      </section>
    );
  }

  const total = Math.max(regiao.total, regiao.itens.length);
  if (regiao.itens.length === 0) {
    return (
      <section className="painel-lista" data-testid="painel-precisa-de-voce" aria-labelledby={idTitulo}>
        {cabecalho()}
        <EstadoVazio icone={CheckCircle2} texto={TEXTO_ESTADO_VAZIO} />
      </section>
    );
  }

  const podeExpandir = regiao.itens.length > LIMITE_LINHAS_RECOLHIDA || total > LIMITE_LINHAS_RECOLHIDA;
  const visiveis = expandido ? regiao.itens : regiao.itens.slice(0, LIMITE_LINHAS_RECOLHIDA);
  return (
    <section className="painel-lista" data-testid="painel-precisa-de-voce" aria-labelledby={idTitulo}>
      {cabecalho(rotuloContagem(total))}
      <ul ref={listaRef} id={idLista} className={expandido ? 'painel-lista__itens painel-lista__itens--expandida' : 'painel-lista__itens'}>
        {comSaindo(visiveis, saindo).map(({ item, saindo: apagando }) => (
          <Linha
            key={item.id}
            item={item}
            agora={agora}
            permissoes={permissoes}
            estado={apagando ? 'saindo' : entrando?.has(item.id) === true ? 'entrando' : undefined}
            brilhos={brilhos}
            reduzido={reduzido}
          />
        ))}
      </ul>
      {expandido && total > regiao.itens.length && (
        <p className="painel-lista__nota" data-testid="painel-lista-nota">
          {`Mostrando as ${regiao.itens.length} mais urgentes de ${total}.`}
        </p>
      )}
      {podeExpandir && (
        <button type="button" className="painel-lista__ver-todos" data-testid="painel-ver-todos" aria-expanded={expandido} aria-controls={idLista} onClick={onAlternar}>
          {rotuloVerTodos(total, expandido)}
        </button>
      )}
    </section>
  );
}

export interface PrecisaDeVoceProps {
  readonly perfil: PerfilHome;
  readonly regiao: RegiaoPrecisaDeVoce | null;
  readonly permissoes: readonly string[];
  readonly agora: Date;
  readonly onTentarNovamente: () => void;
  /** Ids de card que brilham agora (`item-{id}` para itens novos). */
  readonly brilhos?: readonly string[];
  /** A mudança atual veio do stream: só então há entrada, saída e FLIP. */
  readonly aoVivo?: boolean;
  readonly reduzido?: boolean;
}

/**
 * Estado das animações da lista: compara a lista nova com a anterior (padrão "estado derivado na renderização") para saber quem
 * entrou e quem saiu, e limpa as marcas quando as animações terminam. Fora do ao vivo (ou com movimento reduzido) não marca nada.
 */
function useListaAnimada(regiao: RegiaoPrecisaDeVoce | null, ativo: boolean): { entrando: ReadonlySet<string>; saindo: readonly ItemSaindo[] } {
  const itens = regiao !== null && regiao.estado === 'MEDIDO' ? regiao.itens : null;
  const [anteriores, setAnteriores] = useState<readonly PrecisaDeVoceItem[] | null>(itens);
  const [entrando, setEntrando] = useState<ReadonlySet<string>>(new Set());
  const [saindo, setSaindo] = useState<readonly ItemSaindo[]>([]);

  if (itens !== anteriores) {
    setAnteriores(itens);
    if (ativo && anteriores !== null && itens !== null) {
      const { novos, removidos } = diferencaDeIds(
        anteriores.map((i) => i.id),
        itens.map((i) => i.id),
      );
      setEntrando(new Set(novos));
      setSaindo(
        removidos.flatMap((id) => {
          const indice = anteriores.findIndex((i) => i.id === id);
          const item = anteriores[indice];
          return item === undefined ? [] : [{ item, indice }];
        }),
      );
    } else {
      setEntrando(new Set());
      setSaindo([]);
    }
  }

  useEffect(() => {
    if (saindo.length === 0) return undefined;
    const id = setTimeout(() => setSaindo([]), DURACAO_SAIDA_MS);
    return () => clearTimeout(id);
  }, [saindo]);

  useEffect(() => {
    if (entrando.size === 0) return undefined;
    const id = setTimeout(() => setEntrando(new Set()), DURACAO_ENTRADA_MS);
    return () => clearTimeout(id);
  }, [entrando]);

  return { entrando, saindo };
}

/** FLIP da reordenação: mede o topo de cada linha a cada renderização e anima de onde estava até onde ficou (transform, 200 ms). */
function useFlip(listaRef: RefObject<HTMLUListElement | null>, ativo: boolean): void {
  const topos = useRef<Map<string, number>>(new Map());
  useLayoutEffect(() => {
    const lista = listaRef.current;
    const novos = new Map<string, number>();
    if (lista !== null) {
      for (const linha of Array.from(lista.children) as HTMLElement[]) {
        const id = linha.getAttribute('data-item-id');
        if (id === null) continue;
        novos.set(id, linha.offsetTop);
        const antes = topos.current.get(id);
        const moveu = antes !== undefined && antes !== linha.offsetTop;
        if (ativo && moveu && !linha.classList.contains('painel-item--saindo') && typeof linha.animate === 'function') {
          linha.animate([{ transform: `translateY(${antes - linha.offsetTop}px)` }, { transform: 'translateY(0)' }], {
            duration: DURACAO_FLIP_MS,
            easing: CURVA_FLIP,
          });
        }
      }
    }
    topos.current = novos;
  });
}

export function PrecisaDeVoce({ perfil, regiao, permissoes, agora, onTentarNovamente, brilhos, aoVivo = false, reduzido = false }: PrecisaDeVoceProps): ReactElement | null {
  const [expandido, setExpandido] = useState(false);
  const listaRef = useRef<HTMLUListElement>(null);
  const animar = aoVivo && !reduzido;
  const { entrando, saindo } = useListaAnimada(regiao, animar);
  useFlip(listaRef, animar);
  return (
    <PrecisaDeVoceVisao
      perfil={perfil}
      regiao={regiao}
      permissoes={permissoes}
      agora={agora}
      expandido={expandido}
      onAlternar={() => setExpandido((atual) => !atual)}
      onTentarNovamente={onTentarNovamente}
      brilhos={brilhos}
      reduzido={reduzido}
      entrando={entrando}
      saindo={saindo}
      listaRef={listaRef}
    />
  );
}
